import React, { useState } from "react";
import { Bell, Building2, ChevronRight, Eye, EyeOff, Globe, HelpCircle, Loader2, Mail, Phone } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../utils/axios";

const CARD = "bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)]";

const SHORTCUTS = [
  { id: "profile",       icon: Building2,  label: "Business Profile", desc: "Name, address and contact details" },
  { id: "notifications", icon: Bell,       label: "Notifications",    desc: "Choose which alerts you receive" },
  { id: "support",       icon: HelpCircle, label: "Help & Support",   desc: "FAQs and ways to reach our team" },
];

const AccountSettings = ({ vendorData, onUpdate, onNavigate }) => {
  const [toggling, setToggling] = useState(false);
  const vendor = vendorData?.vendor;
  const isLive = vendor?.isLive || false;

  const handleToggleLive = async () => {
    try {
      setToggling(true);
      const res = await api.patch("/vendors/toggle-live");
      if (res.data.success) {
        toast.success(res.data.message);
        onUpdate?.();
      }
    } catch {
      toast.error("Failed to update visibility");
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Account summary */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-gradient-to-br from-purple-50 via-white to-pink-50 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-6 sm:p-7">
        <div className="pointer-events-none absolute -top-20 -right-16 h-56 w-56 rounded-full bg-purple-200/40 blur-3xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-16 h-16 flex-shrink-0 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20 flex items-center justify-center font-['Manrope'] text-2xl font-extrabold text-white">
            {vendor?.business_name?.charAt(0)?.toUpperCase() || <Building2 className="w-7 h-7" />}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-['Manrope'] text-xl sm:text-2xl font-extrabold tracking-tight text-gray-900 truncate">
              {vendor?.business_name || "Your business"}
            </h3>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-gray-600">
              {vendor?.contactDetails?.email && (
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <Mail className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  <span className="truncate">{vendor.contactDetails.email}</span>
                </span>
              )}
              {vendor?.contactDetails?.phone && (
                <span className="inline-flex items-center gap-1.5">
                  <Phone className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  {vendor.contactDetails.phone}
                </span>
              )}
            </div>
          </div>
          <span className={`inline-flex items-center gap-1.5 self-start sm:self-center rounded-full border px-3 py-1.5 text-xs font-semibold ${isLive ? "border-green-200 bg-green-50 text-green-700" : "border-gray-200 bg-white text-gray-600"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-green-500" : "bg-gray-400"}`} />
            {isLive ? "Visible to customers" : "Hidden from customers"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        {/* Visibility */}
        <div className={`${CARD} p-5 sm:p-6`}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Visibility</p>
          <div className="mt-4 flex items-center gap-4">
            <div className={`w-11 h-11 flex-shrink-0 rounded-xl flex items-center justify-center ${isLive ? "bg-purple-50" : "bg-gray-100"}`}>
              <Globe className={`w-5 h-5 ${isLive ? "text-purple-600" : "text-gray-400"}`} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-bold text-gray-900">Profile Visibility</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {isLive ? "Your profile is visible to customers" : "Your profile is hidden from customers"}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isLive}
              aria-label="Profile visibility"
              onClick={handleToggleLive}
              disabled={toggling}
              className={`relative w-11 h-6 flex-shrink-0 rounded-full transition-colors duration-200 ${
                isLive ? "bg-gradient-to-r from-purple-600 to-pink-600" : "bg-gray-200"
              } ${toggling ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow flex items-center justify-center transition-transform duration-200 ${
                  isLive ? "translate-x-5" : "translate-x-0"
                }`}
              >
                {toggling && <Loader2 className="w-3 h-3 text-gray-400 animate-spin" />}
              </span>
            </button>
          </div>
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-gray-200/70 bg-gray-50/60 p-4">
            {isLive ? <Eye className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" /> : <EyeOff className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />}
            <p className="text-sm text-gray-600 leading-relaxed">
              {isLive
                ? "Customers can find your business on the marketplace. Switch this off to hide your profile."
                : "Customers cannot find your business on the marketplace. Switch this on to show your profile."}
            </p>
          </div>
        </div>

        {/* Shortcuts to related pages */}
        <div className={`${CARD} p-5 sm:p-6`}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">More settings</p>
          <div className="mt-3 space-y-1">
            {SHORTCUTS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavigate?.(item.id)}
                className="group w-full flex items-center gap-4 rounded-xl px-2 py-3 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-purple-50 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-purple-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">{item.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                </div>
                <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-400 transition-transform group-hover:translate-x-1" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
